/**
 * Large 3-column drawer for editing a single workflow node — n8n style.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  [icon] http_sink                                  [Run] [X] │
 *   │  POST · enqueues HTTP delivery into the outbox                │
 *   ├──────────────────────┬────────────────────┬───────────────────┤
 *   │   INPUT              │   PARAMETERS       │   OUTPUT          │
 *   │  Last seen           │   (config form)    │   Result / Logs / │
 *   │  payloads picker     │                    │   Trace tabs      │
 *   └──────────────────────┴────────────────────┴───────────────────┘
 *
 * - INPUT pane: pulls /workflows/{id}/nodes/{nid}/recent-inputs.
 *   Source nodes show real history; intermediate nodes show a hint
 *   that per-node capture isn't wired yet — user can paste a payload
 *   manually into the JSON box.
 * - PARAMETERS pane: the existing config form, with more room.
 * - OUTPUT pane: appears only after a Run. Tabs Result / Logs / Trace.
 *
 * The Run button uses the input shown in the INPUT pane (selected
 * historical, or manually edited).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Bug,
  CheckCircle2,
  Clock,
  Copy,
  Info,
  Loader2,
  Play,
  Save,
  Trash2,
} from "lucide-react";
import * as React from "react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { CredentialPicker } from "@/components/credentials/CredentialPicker";
import { BranchesEditor, type SinkBranch } from "@/components/workflows/BranchesEditor";
import { CallMappingEditor } from "@/components/workflows/CallMappingEditor";
import { CodeNodeDebugPanel } from "@/components/workflows/CodeNodeDebugPanel";
import { ExpressionField } from "@/components/workflows/ExpressionField";
import { KeyValueEditor } from "@/components/workflows/KeyValueEditor";
import { parseCurl } from "@/components/workflows/curlImport";
import { bindingsDtsFromSampleText } from "@/components/workflows/expressionBindings";
import { representativeData, upstreamBase } from "@/components/workflows/expressionPicker";
import type { ExprPreview } from "@/lib/pagePreview";
import { ConditionEditor } from "@/components/apps/pageBuilder/ConditionEditor";
import { ItemBatchViewer } from "@/components/workflows/ItemBatchViewer";
import { RunSelector } from "@/components/workflows/RunSelector";
import { runSlicesOf, sliceRun } from "@/lib/workflows/nodeRuns";
import {
  carryConfigToAction,
  siblingActionsFor,
} from "@/components/workflows/integrationActions";
import {
  nodesBackLabel,
  upstreamNodesByDistance,
  type UpstreamNode,
} from "@/components/workflows/nodeGraph";
import { OutcomeRulesEditor, type OutcomeRule } from "@/components/workflows/OutcomeRulesEditor";
import { SmartPicker } from "@/components/workflows/SmartPicker";
import { WebhookListenDialog } from "@/components/workflows/WebhookListenDialog";
import { ImageNodeEditor, IMAGE_CONFIG_FIELDS } from "@/components/workflows/ImageNodeEditor";
import { SwitchCasesEditor, type SwitchCase } from "@/components/workflows/SwitchCasesEditor";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/tooltip";
import type { CredentialType } from "@/lib/api-credentials";
import { ingressApi, type IngressSource, type IngressType } from "@/lib/api-ingress";
import {
  workflowExecutionsApi,
  workflowsApi,
  type CatalogNode,
  type NodeExecutionSummary,
  type NodeKind,
  type RunSingleNodeResponse,
  type TestRunNodeTrace,
  type TriggerStrategy,
  type WorkflowDefinition,
  type WorkflowNode,
} from "@/lib/api-workflows";
import { useLiveEvents } from "@/lib/eventStream";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// JSON Schema helpers
// ---------------------------------------------------------------------------

interface JsonSchema {
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
}

interface JsonSchemaProperty {
  type?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  /** For ``type: "array"`` fields whose items are an enum (e.g. a
   *  ``list[Literal[...]]`` like row_changed's ``events``) — drives the
   *  multi-checkbox renderer. */
  items?: { enum?: unknown[]; type?: string };
  /** For ``type: "object"`` fields — a dict[str,str] declares
   *  ``additionalProperties: {type: "string"}`` (→ KeyValueEditor);
   *  dict[str,Any] declares true/{} (→ JSON textarea). */
  additionalProperties?: { type?: string } | boolean;
  minimum?: number;
  maximum?: number;
  anyOf?: Array<{ type?: string }>;
  "x-credential-type"?: string[];
  /** When set, render a SmartPicker for this picker kind instead of
   *  the generic text/number input. See SmartPicker.tsx for kinds. */
  "x-tray-picker"?: string;
  /** ADR-0009 expression marker (single source of truth from the
   *  backend catalog): "template" → Fixed|ƒx toggle + {{ }} interpolation;
   *  "code" → always a raw-JS editor. Absent → plain fixed control. */
  "x-tray-expression"?: "template" | "code";
  /** Boolean-predicate field (filter/if): render the shared visual
   *  ConditionEditor (Visual | Expression) instead of raw Monaco. */
  "x-tray-condition"?: boolean;
  /** ADR-0009 — optional section grouping. Fields sharing a group label
   *  are rendered together inside a titled, bordered box (n8n-style
   *  "Body" / "Connection" sections). Absent → top-level field. */
  "x-tray-group"?: string;
  /** ADR-0009 — conditional visibility. Map of sibling-field → required
   *  value(s); the field renders only when EVERY entry matches the
   *  current config (n8n displayOptions.show). e.g.
   *  `{ body_content_type: "json" }`. */
  "x-tray-show-when"?: Record<string, unknown>;
}

const SOURCE_NODE_INGRESS_TYPE: Partial<Record<NodeKind, IngressType>> = {
  webhook_source: "webhook",
  sftp_source: "sftp_pull",
  file_watch_source: "file_watch",
  db_poll_source: "db_poll",
  http_cron_source: "http_cron_pull",
};

const MULTILINE_FIELDS = new Set([
  "expression",
  "predicate",
  "items_path",
  "key_path",
  "code",
  "path_template",
]);

function effectiveType(prop: JsonSchemaProperty): string {
  if (prop.enum) return "enum";
  if (prop.type) return prop.type;
  if (prop.anyOf) {
    const non_null = prop.anyOf.find((a) => a.type && a.type !== "null");
    if (non_null?.type) return non_null.type;
  }
  return "string";
}

const CATEGORY_COLOUR: Record<string, string> = {
  source: "bg-emerald-100 text-emerald-700 border-emerald-300",
  sink: "bg-rose-100 text-rose-700 border-rose-300",
  transform: "bg-sky-100 text-sky-700 border-sky-300",
  control: "bg-amber-100 text-amber-700 border-amber-300",
  io_canonical: "bg-violet-100 text-violet-700 border-violet-300",
  lookup: "bg-zinc-100 text-zinc-700 border-zinc-300",
  plugin: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface NodeEditorDrawerProps {
  node: WorkflowNode | null;
  spec: CatalogNode | null;
  /** Full node catalog. Used to offer the Action switcher for integration
   *  nodes (ADR-0011): a Postgres node can switch select↔insert↔… in place,
   *  rewriting node.kind and carrying shared config across. */
  catalog?: CatalogNode[];
  workflowId: string;
  /** When the workflow is app-kind (ADR-0010 D10), the bound app's id.
   *  Threaded down to the SmartPicker so the ``app_table`` picker can list
   *  the app's data tables. Undefined for canonical/orchestration. */
  appId?: string;
  /** All nodes currently on the canvas. Used by node_id SmartPicker
   *  fields (e.g. RouterConfig target). */
  nodesInCanvas?: { id: string; label: string | null; kind: string }[];
  /** Canvas edges — the INPUT pane uses them to compute the upstream
   *  node picker ("N nodes back", ADR-0012). */
  edges?: { source: string; target: string }[];
  /** Per-node output from the most recent test-run (node id → payload).
   *  Lets the INPUT pane show an upstream node's data when picked. */
  nodeOutputs?: Record<string, unknown>;
  /** Each node's `log_outputs`, so the Input pane can page an UPSTREAM
   *  node's loop iterations (its run windows ride there). */
  nodeLogOutputs?: Record<string, Record<string, unknown>>;
  /** Full per-node traces (input + output + status + logs) from the most
   *  recent LIVE test-run, keyed by node id. When the drawer opens (or the
   *  user switches nodes) it seeds the INPUT and OUTPUT panes from this node's
   *  trace, so re-opening a node after a test-run still shows what flowed
   *  through it — instead of blanking because the transient "Run node" result
   *  was cleared on node-change. Past-run view uses ``viewedRunNodeExecutions``
   *  instead. */
  nodeTraces?: Record<string, TestRunNodeTrace>;
  /** Source-handle values of outgoing edges from the currently-edited
   *  node. Used by SwitchCasesEditor to surface orphan warnings when a
   *  case is removed. */
  outgoingEdgeHandles?: string[];
  /** Per-node execution rows from the currently-viewed historical run,
   *  if any. Drives the read-only "what happened here" view: when set
   *  the drawer shows the recorded input/output instead of the live
   *  config form. One node can have multiple rows when iterations
   *  fan-out (SPLIT/LOOP). */
  viewedRunNodeExecutions?: NodeExecutionSummary[];
  /** When true, the drawer hides edit affordances (Save, Delete, Run
   *  button) and renders the parameters as read-only. Always true when
   *  ``viewedRunNodeExecutions`` is set. */
  readOnly?: boolean;
  onClose: () => void;
  onChange: (id: string, patch: Partial<WorkflowNode>) => void;
  onDelete: (id: string) => void;
  /** Optional: lets the drawer's "Duplicate" button clone the node
   *  via the editor's id-counter logic. Hidden when not provided. */
  onDuplicate?: (id: string) => void;
  /** Snapshot of the live editor canvas. The modal needs this to fire
   *  a test-run from the trigger up to this node ("Execute Node"). */
  buildDefinition?: () => WorkflowDefinition;
  /** Called when Execute Node completes. The editor uses it to mirror
   *  the per-node statuses on the canvas (ring colour) and to populate
   *  the bottom logs panel with the full trace — same wiring as the
   *  TestRunDrawer. Without this the modal would run silently with no
   *  visible feedback on the canvas after the user closes it. */
  onExecuteComplete?: (result: import("@/lib/api-workflows").TestRunResponse) => void;
  /** TE-F step-by-step: when set (a pinned execution of the SAME structural
   *  graph exists AND this node is the pinned node or downstream of it),
   *  "Execute Node" RESUMES from it — reusing the pinned upstream + the parent
   *  run's input (no re-prompt), re-running only THIS node — instead of a fresh
   *  run from the trigger. Null → fresh run (upstream / unrelated node). */
  resumeFromExecutionId?: string | null;
  /** The button semantics for the current node vs the pinned node:
   *  ``"rerun"`` (same node → "Re-execute node"), ``"continue"`` (downstream →
   *  "Continue execution"), or null (fresh → "Execute Node"). */
  resumeKind?: "rerun" | "continue" | null;
  /** Canvas "Run node" affordance: a monotonically-increasing token. Each
   *  increment (while the drawer is open for a node) auto-fires the same
   *  Execute Node flow the header button triggers. Undefined → no auto-run. */
  autoExecuteToken?: number;
  /** Live I/O (fetch-on-demand): the currently-RUNNING test-run's execution
   *  id. While set, the drawer reacts to the open node's ``node_finished``
   *  SSE by fetching its just-committed trace (progressive persistence on
   *  the sync path) — so the Input/Output panes fill DURING the run instead
   *  of only when the whole run returns. Null when no run is live. */
  liveExecutionId?: string | null;
}

export function NodeEditorDrawer({
  node,
  spec,
  catalog = [],
  workflowId,
  appId,
  nodesInCanvas,
  edges = [],
  nodeOutputs = {},
  nodeLogOutputs = {},
  nodeTraces = {},
  outgoingEdgeHandles = [],
  viewedRunNodeExecutions,
  readOnly = false,
  onClose,
  onChange,
  onDelete,
  onDuplicate,
  buildDefinition,
  onExecuteComplete,
  resumeFromExecutionId = null,
  resumeKind = null,
  autoExecuteToken,
  liveExecutionId = null,
}: NodeEditorDrawerProps) {

  // Reset interactive state every time we focus a different node so
  // the OUTPUT pane doesn't leak the previous node's result.
  const [inputText, setInputText] = React.useState<string>("{}");
  const [inputError, setInputError] = React.useState<string | null>(null);
  const [runResult, setRunResult] = React.useState<RunSingleNodeResponse | null>(null);
  const [outputTab, setOutputTab] = React.useState<"result" | "logs" | "trace">("result");
  // Center column tab: per-kind config (Parameters) vs node-level Settings
  // (n8n parity — execute_once / always_output_data / retry / on_error /
  // notes live on WorkflowNode, not the per-kind config).
  const [paramTab, setParamTab] = React.useState<"parameters" | "settings">("parameters");
  // n8n-style "Import cURL": paste a curl command to pre-fill method /
  // url / headers / body. Only offered on HTTP nodes.
  const [curlOpen, setCurlOpen] = React.useState(false);
  const [curlText, setCurlText] = React.useState("");
  const [curlError, setCurlError] = React.useState<string | null>(null);
  // ADR-0067 S4 — interactive debugger overlay for a js_code_transform node.
  const [debugOpen, setDebugOpen] = React.useState(false);
  // Close the debugger when switching to a different node.
  React.useEffect(() => {
    setDebugOpen(false);
  }, [node?.id]);
  // ADR-0009 #4.3 — ambient TS declarations for the expression editor's
  // IntelliSense, regenerated from the node's sample input so ``$json.``
  // suggests the keys actually present in the payload.
  // ADR-0012 — labels of the OTHER canvas nodes feed $('Label') completions.
  const otherNodeLabels = React.useMemo(
    () =>
      (nodesInCanvas ?? [])
        .filter((n) => n.label && n.id !== node?.id)
        .map((n) => n.label as string),
    [nodesInCanvas, node?.id],
  );
  // F4 (ADR-0068) — per-node sample data for typed `$('Node')` IntelliSense
  // AND the live-preview `$('Node')` refs. `{label: <representative item data>}`
  // over the upstream nodes whose output was captured on the last run.
  const previewNodeRefs = React.useMemo<Record<string, unknown>>(() => {
    const refs: Record<string, unknown> = {};
    for (const n of nodesInCanvas ?? []) {
      if (n.label && n.id !== node?.id && n.id in nodeOutputs) {
        refs[n.label] = representativeData(nodeOutputs[n.id]);
      }
    }
    return refs;
  }, [nodesInCanvas, nodeOutputs, node?.id]);
  const previewSample = React.useMemo<Record<string, unknown>>(() => {
    const t = (inputText ?? "").trim();
    if (!t) return {};
    try {
      const d = representativeData(JSON.parse(t));
      return d && typeof d === "object" && !Array.isArray(d)
        ? (d as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }, [inputText]);
  const jsBindingsDts = React.useMemo(
    () => bindingsDtsFromSampleText(inputText, otherNodeLabels, previewNodeRefs),
    [inputText, otherNodeLabels, previewNodeRefs],
  );
  // F4 (ADR-0068) — the input data-picker seam. The last-focused ƒx field
  // registers its "insert at caret" fn here (via ExpressionField's
  // ``registerInsert``); clicking a leaf in the Input pane's DataPickerTree
  // drops the ``{{ … }}`` binding into it. Stable identity so the field's
  // register/cleanup effect doesn't re-fire.
  const activeInsertRef = React.useRef<((text: string) => void) | null>(null);
  const registerInsert = React.useCallback(
    (fn: ((text: string) => void) | null) => {
      // A field registers its insert fn on focus (non-null). We IGNORE the
      // unmount clear (`null`): a NON-active ƒx field disappearing (e.g. an
      // `x-tray-show-when` field hidden by a sibling edit) must not steal the
      // active insert target. A disposed ACTIVE editor is handled below.
      if (fn) activeInsertRef.current = fn;
    },
    [],
  );
  const insertBinding = React.useCallback((expr: string) => {
    const fn = activeInsertRef.current;
    if (!fn) return;
    try {
      fn(expr);
    } catch {
      // The active field's Monaco editor was disposed (its node/field
      // unmounted) — drop the stale target so the next focus re-arms it.
      activeInsertRef.current = null;
    }
  }, []);

  // F4 (ADR-0068) — server-side live preview. The ƒx field renders
  // `= <resolved value>` by round-tripping the template to the engine's
  // QuickJS. `resolvePreview` closes over the CURRENT Input sample + `$('Node')`
  // refs and is memoised on THEM — so editing the test-input JSON refreshes the
  // `= value` line, while typing the expression (which changes the field VALUE,
  // not the sample) keeps the debounce stable (the value is ExpressionField's
  // own effect dep, not this identity).
  const resolvePreview = React.useCallback(
    async (template: string): Promise<ExprPreview | null> => {
      // Nothing to preview for a fixed value or the empty `{{ }}` scaffold.
      if (!template.includes("{{")) return null;
      if (template.replace(/\{\{\s*\}\}/g, "").trim() === "") return null;
      try {
        const res = await workflowsApi.previewExpression({
          template,
          sample: previewSample,
          node_refs: previewNodeRefs,
        });
        return res.ok
          ? { ok: true, display: res.display ?? "" }
          : {
              ok: false,
              error: res.error ?? "error",
              needsUpstream: res.needs_upstream ?? false,
            };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : String(e) };
      }
    },
    [previewSample, previewNodeRefs],
  );
  // Field options for the visual ConditionEditor (filter/if predicates): the
  // top-level keys of the sample input as `$json.<key>` so authors pick a field
  // instead of typing it.
  const conditionFields = React.useMemo(() => {
    try {
      const o = JSON.parse(inputText || "{}");
      if (o && typeof o === "object" && !Array.isArray(o)) {
        return Object.keys(o).map((k) => `$json.${k}`);
      }
    } catch {
      /* invalid sample → no suggestions, type freely */
    }
    return [];
  }, [inputText]);
  // ADR-0012 — upstream nodes for the INPUT pane "N nodes back" picker.
  const upstreamNodes = React.useMemo<UpstreamNode[]>(
    () =>
      node
        ? upstreamNodesByDistance(nodesInCanvas ?? [], edges, node.id)
        : [],
    [nodesInCanvas, edges, node],
  );
  // Latest traces read through a ref so the node-change effect stays keyed on
  // ``node?.id`` alone (a traces-object identity change must NOT re-seed and
  // clobber a manual "Run node" result the user just produced).
  const nodeTracesRef = React.useRef(nodeTraces);
  nodeTracesRef.current = nodeTraces;
  const previousNodeId = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (node?.id !== previousNodeId.current) {
      previousNodeId.current = node?.id ?? null;
      setInputError(null);
      // Seed the INPUT / OUTPUT panes from the last test-run's trace for this
      // node, if any — so re-opening a node after a test-run keeps showing
      // what actually flowed through it. Falls back to the empty state for a
      // node that hasn't run yet.
      const trace = node ? nodeTracesRef.current[node.id] : undefined;
      if (trace) {
        setInputText(
          trace.input !== undefined ? JSON.stringify(trace.input, null, 2) : "{}",
        );
        setRunResult(traceToResult(trace, trace.status === "failed" ? "failed" : "succeeded"));
        if (!trace.output && Object.keys(trace.log_outputs ?? {}).length > 0) {
          setOutputTab("logs");
        } else {
          setOutputTab("result");
        }
      } else {
        setInputText("{}");
        setRunResult(null);
        setOutputTab("result");
      }
    }
  }, [node?.id]);

  // (B step 3): the recent-payloads query is gone. The Input pane no
  // longer pulls "last seen payloads from past executions" — that
  // surface fought the n8n mental model where the input is what
  // actually flowed in from the upstream node in a real run.
  // ``Run up to here`` from the modal header is now the way to
  // populate the Input pane with live data.

  // ── Live I/O (fetch-on-demand) ───────────────────────────────────────────
  // While a test-run is LIVE and this drawer is open, the open node's
  // ``node_finished`` SSE triggers a fetch of its just-committed trace
  // (progressive persistence flushes each node's row BEFORE the event
  // fires, so the read never races the write) and seeds the Input/Output
  // panes — the operator sees the node's real I/O DURING the run instead
  // of a spinner until the whole run returns. Keyed by event id so one
  // ping fetches once; a 404 is "not yet" (the end-of-run ``nodeTraces``
  // seeding is the safety net).
  const liveEvents = useLiveEvents();
  const handledLiveEventRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!liveExecutionId || !node) return;
    // Buffer is NEWEST-FIRST: the first match is the node's latest ping.
    // The workflow_id filter keeps a same-named SUB-workflow node (child
    // events carry the child's workflow_id) off this canvas's drawer.
    const ev = liveEvents.events.find(
      (e) =>
        e.kind === "node_finished" &&
        e.execution_id === liveExecutionId &&
        e.workflow_id === workflowId &&
        e.node_id === node.id,
    );
    if (!ev) return;
    const evKey = `${node.id}:${ev.id}`;
    if (handledLiveEventRef.current === evKey) return;
    handledLiveEventRef.current = evKey;
    const nodeId = node.id;
    void workflowExecutionsApi
      .nodeTrace(liveExecutionId, nodeId)
      .then((t) => {
        // The user may have switched node while the fetch was in flight —
        // never seed another node's panes with this one's data.
        if (previousNodeId.current !== nodeId) return;
        if (t.input !== undefined && t.input !== null) {
          setInputText(JSON.stringify(t.input, null, 2));
        }
        setRunResult({
          status:
            t.status === "succeeded"
              ? "succeeded"
              : t.status === "skipped"
                ? "skipped"
                : "failed",
          output_payload: t.output ?? null,
          error: t.error,
          log_outputs: (t.log_outputs ?? {}) as Record<string, unknown>,
          duration_ms: t.duration_ms ?? 0,
        });
        setOutputTab("result");
      })
      .catch(() => {
        /* 404 = row not visible yet / auth blip — the end-of-run seed covers it */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEvents.generation, liveExecutionId, node?.id, workflowId]);

  // SOURCE nodes carry an ingress_source_id binding — picker query.
  const ingressType = node ? (SOURCE_NODE_INGRESS_TYPE[node.kind as NodeKind] ?? null) : null;
  const ingressQuery = useQuery({
    queryKey: ["ingress", "for-node-picker", ingressType],
    queryFn: () =>
      // page_size is constrained to {25, 50, 100} by PageParams. 200
      // would silently 422 and the dropdown would look empty — visually
      // indistinguishable from "no ingress sources" so the operator
      // sees a bare UUID in the field instead of the name.
      ingressApi.list({ type: ingressType ?? undefined, page_size: 100 }),
    enabled: ingressType !== null,
    select: (p) => p.items,
  });
  const ingressOptions = React.useMemo<ComboboxOption[]>(() => {
    return (ingressQuery.data ?? []).map((s: IngressSource) => ({
      value: s.id,
      label: s.name,
      description: s.enabled ? "enabled" : "paused",
    }));
  }, [ingressQuery.data]);

  // "Execute Node" — run the workflow from the trigger up to and
  // including this node. Replaces the legacy single-node Run + the
  // step-debug "Run up to here" with a single affordance. The Output
  // pane shows what this node emitted; the Input pane mirrors what
  // the upstream actually fed in.
  //
  // The trigger payload is owned by the trigger strategy:
  //   * auto_payload (cron/schedule)  → backend synthesises, we send null
  //   * manual_input                  → inline prompt opens (PromptStrategyDialog)
  //   * external_hit (webhook)        → inline prompt with URL/curl
  //
  // When the user is mid-prompt and clicks Run, we re-enter this
  // mutation with ``manualPayloadOverride`` populated.
  const [promptStrategy, setPromptStrategy] = React.useState<TriggerStrategy | null>(null);
  // When the run is triggered by a webhook capture, the test-capture
  // endpoint is parked waiting for the result. We ref the token so
  // ``executeMutation.onSuccess`` can publish it back without making
  // the token a top-level dependency.
  const pendingRespondTokenRef = React.useRef<string | null>(null);

  // ─── Resizable column widths (n8n-style splits) ───────────────────────
  // Two drag handles between the 3 panes (Input | Params | Output).
  // Widths persist to localStorage so the user's chosen layout sticks
  // across modal opens / page reloads. The center Parameters pane is
  // the elastic one (grows to fill whatever's left).
  const [inputW, setInputW] = React.useState<number>(() => {
    const v = Number(localStorage.getItem("tray:node-modal:inputW"));
    return Number.isFinite(v) && v >= 200 && v <= 600 ? v : 320;
  });
  const [outputW, setOutputW] = React.useState<number>(() => {
    const v = Number(localStorage.getItem("tray:node-modal:outputW"));
    return Number.isFinite(v) && v >= 240 && v <= 800 ? v : 384;
  });
  React.useEffect(() => {
    localStorage.setItem("tray:node-modal:inputW", String(inputW));
  }, [inputW]);
  React.useEffect(() => {
    localStorage.setItem("tray:node-modal:outputW", String(outputW));
  }, [outputW]);

  const executeMutation = useMutation({
    mutationFn: async (manualPayloadOverride?: unknown) => {
      if (!buildDefinition) {
        throw new Error("executor not wired (buildDefinition missing) — please report");
      }
      const definition = buildDefinition();

      // TE-F step-by-step: a pinned run of the SAME graph exists → RESUME.
      // The backend replays the parent's input (no re-prompt) + reuses the
      // pinned upstream outputs, re-running only THIS node (the stop node is
      // excluded from the pinned map server-side). Skip the trigger-strategy
      // resolution entirely — there's no payload to ask for. Only for a real
      // run (not the manual-override re-entry, which already has a payload).
      if (manualPayloadOverride === undefined && resumeFromExecutionId) {
        return workflowsApi.testRun(workflowId, {
          definition,
          resume_from_execution_id: resumeFromExecutionId,
          stop_after_node_id: node!.id,
        });
      }

      // Resolve the strategy when we don't already have one in flight.
      // The query result lives in TanStack cache but we don't depend
      // on the hook here (we'd violate hook rules inside a mutation
      // closure) — fetch fresh on demand.
      let strategy: TriggerStrategy | null = null;
      if (manualPayloadOverride === undefined) {
        const resp = await workflowsApi.triggerStrategies(workflowId, {
          definition,
        });
        // Pick the first source — multi-source DAGs aren't reachable
        // from the per-node modal entry (the user would use the
        // canvas-level Test Run drawer for that disambiguation).
        strategy = resp.strategies[0] ?? null;
        if (strategy && strategy.kind !== "auto_payload") {
          // Need user input — surface the prompt and let it call us
          // back via the override path.
          setPromptStrategy(strategy);
          // Returning a special sentinel skips the rest of the flow.
          // (TanStack treats this as success; we just don't update
          // state since there's no run yet.)
          return null;
        }
      }

      return workflowsApi.testRun(workflowId, {
        definition,
        // When the strategy is auto_payload we send no payload — the
        // backend synthesises it. When the user typed one in the
        // prompt we forward it verbatim.
        payload: manualPayloadOverride,
        stop_after_node_id: node!.id,
      });
    },
    onSuccess: (res) => {
      // Strategy prompt path returns null (the prompt opened) — no
      // result to render yet, just wait for the user.
      if (res === null) return;

      // Notify the parent editor so the canvas ring lights up + the
      // bottom logs panel auto-opens with the trace. Mirrors what the
      // TestRunDrawer does on its own onRunComplete. Without this, the
      // run happened "in private" inside the modal and the canvas
      // stayed cold.
      onExecuteComplete?.(res);

      // Listen-for-test follow-through: if this run came from a webhook
      // capture, the test-capture endpoint is parked. Hand it the
      // workflow output so the curl caller sees it instead of just
      // {"received": true}. Fire-and-forget; the slot might have
      // already timed out, that's OK.
      const respondToken = pendingRespondTokenRef.current;
      if (respondToken) {
        pendingRespondTokenRef.current = null;
        workflowsApi
          .respondTestWebhook(workflowId, respondToken, {
            status_code: res.status === "succeeded" ? 200 : 500,
            body:
              res.status === "succeeded"
                ? res.final_payload
                : { error: res.error ?? "workflow_failed" },
          })
          .catch(() => undefined);
      }

      // Find THIS node's trace in the result. If the run didn't
      // reach the node (upstream failure), we still surface what we
      // can: the trace for the last node that actually ran, plus
      // the global error message.
      const trace = res.node_traces.find((t) => t.node_id === node!.id);
      if (trace) {
        setRunResult(traceToResult(trace, res.status));
        if (trace.input !== undefined) {
          setInputText(JSON.stringify(trace.input, null, 2));
        }
        if (!trace.output && Object.keys(trace.log_outputs ?? {}).length > 0) {
          setOutputTab("logs");
        } else {
          setOutputTab("result");
        }
      } else {
        // Node didn't execute (upstream failure before it). Surface
        // the run-level error in the Output pane.
        setRunResult({
          status: "failed",
          output_payload: null,
          error: res.error ?? "the run did not reach this node — check the upstream traces",
          log_outputs: {},
          duration_ms: 0,
        });
        setOutputTab("result");
      }
    },
    onError: (err: Error) => {
      setRunResult({
        status: "failed",
        output_payload: null,
        error: err.message,
        log_outputs: {},
        duration_ms: 0,
      });
      setOutputTab("result");
    },
  });

  // Keep the Dialog mounted across node changes so Radix can run its
  // close animation cleanly when the user dismisses it — early-
  // returning ``null`` here unmounts mid-animation and was part of
  // the flicker loop we hit before.
  const isOpen = node !== null;

  // Canvas "Run node" affordance: each new ``autoExecuteToken`` fires the
  // same Execute Node flow the header button does (prompting for trigger
  // input when needed). Guarded to fire once per token bump, only while the
  // drawer is genuinely open for an editable node.
  const lastAutoTokenRef = React.useRef(autoExecuteToken);
  React.useEffect(() => {
    if (autoExecuteToken === undefined) return;
    if (autoExecuteToken === lastAutoTokenRef.current) return;
    lastAutoTokenRef.current = autoExecuteToken;
    if (
      isOpen &&
      !readOnly &&
      buildDefinition &&
      node &&
      !executeMutation.isPending
    ) {
      executeMutation.mutate(undefined);
    }
    // executeMutation identity is stable enough; we intentionally key only
    // on the token so a re-render can't re-fire the run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExecuteToken, isOpen, readOnly, node]);

  // Fullscreen-ish modal styling: 96vw × 92vh, capped to ~1400px so
  // the 3-column layout still looks like a real app on 27" monitors.
  // The `!` overrides are needed because DialogContent's base style
  // sets sm:max-w-lg / sm:h-auto which would otherwise win on Tailwind
  // specificity grounds.
  const dialogContentClass =
    "p-0 flex flex-col " +
    "sm:!max-w-[min(98vw,2200px)] sm:!w-[min(98vw,2200px)] " +
    "sm:!max-h-[96vh] sm:!h-[96vh]";

  if (!node) {
    // Render an empty Dialog so the open/close transition has a target.
    return (
      <Dialog open={false} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className={dialogContentClass}>
          <DialogTitle className="sr-only">Node editor</DialogTitle>
        </DialogContent>
      </Dialog>
    );
  }

  const schema = (spec?.config_schema ?? {}) as JsonSchema;
  const properties = schema.properties ?? {};
  const required = new Set(schema.required ?? []);
  const category = spec?.category ?? "transform";
  const colourCls = CATEGORY_COLOUR[category] ?? "bg-muted text-foreground border-border";

  // ADR-0011 — Action switcher. When this node belongs to an Integration
  // (Postgres), list its sibling Actions so the user can change action in
  // place without deleting + re-dropping. Switching rewrites node.kind and
  // carries over config fields that exist in BOTH actions' schemas.
  const integrationId = spec?.integration ?? null;
  const siblingActions = siblingActionsFor(catalog, integrationId);
  const actionOptions: ComboboxOption[] = siblingActions.map((a) => ({
    value: a.kind,
    label: a.label,
  }));

  function switchAction(newKind: string | null) {
    if (readOnly || !node || !newKind || newKind === node.kind) return;
    const newSpec = catalog.find((n) => n.kind === newKind);
    onChange(node.id, {
      kind: newKind as NodeKind,
      config: carryConfigToAction(node.config, newSpec),
    });
  }

  function updateConfig(field: string, value: unknown) {
    // No-op in read-only / view-past-run mode: keeps every existing
    // form input from accidentally mutating canvas state when the
    // user is just inspecting a historical execution.
    if (readOnly) return;
    onChange(node!.id, {
      config: { ...node!.config, [field]: value },
    });
  }

  function updateIngressBinding(sourceId: string | null) {
    const found = (ingressQuery.data ?? []).find((s: IngressSource) => s.id === sourceId);
    onChange(node!.id, {
      config: {
        ...node!.config,
        ingress_source_id: sourceId ?? "",
        ingress_source_name: found?.name ?? null,
      },
    });
  }

  function updateLabel(value: string) {
    onChange(node!.id, { label: value || null });
  }

  function updateSetting(patch: Partial<WorkflowNode>) {
    onChange(node!.id, patch);
  }

  function applyCurl() {
    try {
      const patch = parseCurl(curlText);
      onChange(node!.id, { config: { ...node!.config, ...patch } });
      setCurlOpen(false);
      setCurlText("");
      setCurlError(null);
    } catch (e) {
      setCurlError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={dialogContentClass}>
        {/* Header */}
        <div
          className={cn(
            "node-editor-header flex flex-row items-start justify-between gap-3",
            "border-b border-[#e0e0e0] dark:border-[#3e3e3e] px-5 sm:px-6 py-3 shrink-0",
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span
                className={
                  "inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider " +
                  colourCls
                }
              >
                {category}
              </span>
              <DialogTitle className="truncate text-sm font-semibold">
                {node.label || spec?.label || node.kind}
              </DialogTitle>
              <span className="font-mono text-[11px] text-muted-foreground">{node.kind}</span>
              <Badge tone="neutral" className="text-[10px]">
                {node.id}
              </Badge>
              {spec?.description && (
                <Tooltip content={spec.description} side="bottom">
                  <button
                    type="button"
                    // ``tabIndex={-1}``: Radix Dialog auto-focuses the first
                    // tabbable element on open — which was THIS icon — and Radix
                    // Tooltip opens on focus, so the description tooltip popped up
                    // unbidden every time the drawer opened. Dropping it from the
                    // tab order keeps the tooltip purely hover/click-triggered.
                    tabIndex={-1}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Node description"
                  >
                    <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 pr-9">
            {!readOnly && buildDefinition && (
              <Button
                size="sm"
                onClick={() => executeMutation.mutate(undefined)}
                disabled={executeMutation.isPending}
                title={
                  resumeKind === "rerun"
                    ? "Re-run THIS node against the already-computed upstream " +
                      "(same input, no prompt) — the run is paused here."
                    : resumeKind === "continue"
                      ? "Continue the step-by-step run: reuse the computed " +
                        "upstream + the same input (no prompt) and run up to " +
                        "this node."
                      : "Run the workflow from the trigger up to (and " +
                        "including) this node. Cron / scheduled triggers fire " +
                        "immediately; manual / webhook triggers prompt for the input."
                }
              >
                {executeMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
                )}
                {resumeKind === "rerun"
                  ? "Re-execute node"
                  : resumeKind === "continue"
                    ? "Continue execution"
                    : "Execute Node"}
              </Button>
            )}
            {readOnly && (
              <Badge tone="neutral" className="font-normal">
                Read-only · viewing past run
              </Badge>
            )}
            {!readOnly && node.kind === "js_code_transform" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDebugOpen(true)}
                title="Debug: esegui il codice passo-passo su una riga campione (breakpoint, step, variabili)"
              >
                <Bug className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                Debug
              </Button>
            )}
            {!readOnly && onDuplicate && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDuplicate(node.id)}
                title="Duplicate this node (⌘D)"
              >
                <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Button>
            )}
            {!readOnly && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (window.confirm(`Delete node "${node.id}"?`)) {
                    onDelete(node.id);
                    onClose();
                  }
                }}
                title="Delete this node"
              >
                <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>

        {/* Body — 3-column grid that fills the remaining modal height.
            ``min-h-0`` is mandatory: without it the grid refuses to
            shrink below its content's intrinsic height and the inner
            panes start scrolling the modal instead of themselves.
            Outer columns are user-resizable via the dividers below;
            the center Parameters pane is the elastic ``1fr``.
            ``relative`` anchors the central slide handle (top-center
            pill) that sits above the column headers. */}
        <div
          className="node-editor-body grid flex-1 min-h-0 gap-0"
          style={{
            gridTemplateColumns: `${inputW}px 6px minmax(0,1fr) 6px ${outputW}px`,
          }}
        >
          {/* INPUT PANE */}
          <InputPane
            inputText={inputText}
            onInputTextChange={setInputText}
            error={inputError}
            upstreamNodes={upstreamNodes}
            nodeOutputs={nodeOutputs}
            nodeLogOutputs={nodeLogOutputs}
            onInsert={insertBinding}
            logOutputs={runResult?.log_outputs}
          />

          {/* Input / Params divider */}
          <ResizeHandle onResize={(dx) => setInputW((w) => Math.max(200, Math.min(600, w + dx)))} />

          {/* PARAMETERS PANE — elastic ``1fr``, fills whatever the
              user leaves after sizing the two outer panes. ``relative``
              anchors the central slide grip; ``overflow-visible`` so
              the grip's top half can spill above the pane edge. */}
          <section className="node-editor-params relative flex h-full min-h-0 flex-col">
            {/* N8N-style central drag handle — always horizontally
                centered on the Parameters column header, regardless
                of input/output widths. Drag horizontally to slide
                the column (Input + Output trade pixels symmetrically). */}
            <CenterSlideHandle
              onSlide={(dx) => {
                setInputW((w) => Math.max(200, Math.min(600, w + dx)));
                setOutputW((w) => Math.max(240, Math.min(800, w - dx)));
              }}
            />
            <div className="flex items-center gap-1 border-b border-border/60 px-3">
              {(["parameters", "settings"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setParamTab(tab)}
                  className={cn(
                    "border-b-2 px-2 py-2 text-[11px] uppercase tracking-wider transition-colors",
                    paramTab === tab
                      ? "border-accent text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {paramTab === "parameters" && (
                <>
                  {!readOnly && (node.kind === "http_request" || node.kind === "http_sink") && (
                    <div className="mb-4">
                      {!curlOpen ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => setCurlOpen(true)}
                        >
                          Import cURL
                        </Button>
                      ) : (
                        <div className="space-y-2 rounded-md border border-border bg-background p-3">
                          <Label className="text-[11px] text-muted-foreground">
                            Paste a cURL command
                          </Label>
                          <textarea
                            autoFocus
                            rows={4}
                            className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs"
                            value={curlText}
                            onChange={(e) => setCurlText(e.target.value)}
                            placeholder={
                              "curl -X POST https://api/x -H 'Content-Type: application/json' -d '{\"a\":1}'"
                            }
                          />
                          {curlError && <p className="text-[11px] text-danger">{curlError}</p>}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              className="text-xs"
                              disabled={!curlText.trim()}
                              onClick={applyCurl}
                            >
                              Import
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setCurlOpen(false);
                                setCurlError(null);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {viewedRunNodeExecutions && viewedRunNodeExecutions.length > 0 && (
                    <div className="mb-4 space-y-2 rounded-md border border-info/40 bg-info/5 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-info">
                        Recorded for this run · {viewedRunNodeExecutions.length} iteration
                        {viewedRunNodeExecutions.length > 1 ? "s" : ""}
                      </div>
                      {viewedRunNodeExecutions.slice(0, 5).map((nx, idx) => (
                        <div
                          key={nx.id}
                          className="space-y-1 rounded border border-border/60 bg-background p-2"
                        >
                          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
                            <span>
                              {viewedRunNodeExecutions.length > 1
                                ? `iter #${nx.iteration}`
                                : "trace"}
                              {" · "}
                              <span
                                className={
                                  nx.status === "failed"
                                    ? "text-danger"
                                    : nx.status === "skipped"
                                      ? "text-muted-foreground"
                                      : "text-success"
                                }
                              >
                                {nx.status}
                              </span>
                            </span>
                            {nx.duration_ms != null && <span>{nx.duration_ms}ms</span>}
                          </div>
                          {nx.error && (
                            <div className="rounded bg-danger/10 px-2 py-1 font-mono text-[11px] text-danger">
                              {nx.error}
                            </div>
                          )}
                          <details className="text-[11px]" open={idx === 0}>
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                              input
                            </summary>
                            <pre className="mt-1 max-h-40 overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px]">
                              {nx.input === undefined || nx.input === null
                                ? "(not captured)"
                                : JSON.stringify(nx.input, null, 2)}
                            </pre>
                          </details>
                          <details className="text-[11px]">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                              output
                            </summary>
                            <pre className="mt-1 max-h-40 overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px]">
                              {nx.output === undefined || nx.output === null
                                ? "(not captured)"
                                : JSON.stringify(nx.output, null, 2)}
                            </pre>
                          </details>
                          {nx.log_outputs && Object.keys(nx.log_outputs).length > 0 && (
                            <details className="text-[11px]">
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                log_outputs
                              </summary>
                              <pre className="mt-1 max-h-40 overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px]">
                                {JSON.stringify(nx.log_outputs, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      ))}
                      {viewedRunNodeExecutions.length > 5 && (
                        <div className="text-[10px] text-muted-foreground">
                          …{viewedRunNodeExecutions.length - 5} more iteration(s) not shown
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-4 space-y-1.5">
                    <Label htmlFor="node-label">Label (optional)</Label>
                    <Input
                      id="node-label"
                      className="bg-background"
                      value={node.label ?? ""}
                      onChange={(e) => updateLabel(e.target.value)}
                      placeholder="e.g. Parse EDI"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Shown on the canvas instead of the node id. Pure cosmetic.
                    </p>
                  </div>
                </>
              )}

              {/* Node Settings (n8n-style). Live on WorkflowNode, not the
                  per-kind config. ADR-0009 Track 2a–2e — own tab. */}
              {paramTab === "settings" && (
                <div className="mb-4 space-y-2.5">
                  <p className="text-[11px] text-muted-foreground">
                    These apply to the node itself, independent of its parameters.
                  </p>

                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!node.execute_once}
                      onChange={(e) => updateSetting({ execute_once: e.target.checked })}
                    />
                    <span className="text-foreground">Execute once</span>
                    <span className="text-muted-foreground">— only the first input item</span>
                  </label>

                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!node.always_output_data}
                      onChange={(e) => updateSetting({ always_output_data: e.target.checked })}
                    />
                    <span className="text-foreground">Always output data</span>
                    <span className="text-muted-foreground">— emit an empty item if none</span>
                  </label>

                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!node.retry_on_fail}
                      onChange={(e) => updateSetting({ retry_on_fail: e.target.checked })}
                    />
                    <span className="text-foreground">Retry on fail</span>
                  </label>
                  {node.retry_on_fail && (
                    <div className="ml-6 flex gap-3">
                      <label className="text-[11px] text-muted-foreground">
                        Max tries
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          value={String(node.max_tries ?? 3)}
                          onChange={(e) =>
                            updateSetting({
                              max_tries: Number(e.target.value) || 1,
                            })
                          }
                          className="mt-1 h-7 w-20 bg-background"
                        />
                      </label>
                      <label className="text-[11px] text-muted-foreground">
                        Wait (ms)
                        <Input
                          type="number"
                          min={0}
                          value={String(node.retry_wait_ms ?? 1000)}
                          onChange={(e) =>
                            updateSetting({
                              retry_wait_ms: Number(e.target.value) || 0,
                            })
                          }
                          className="mt-1 h-7 w-24 bg-background"
                        />
                      </label>
                    </div>
                  )}

                  {/* Execution mode — shown only when the catalog spec allows a
                      choice (supported_modes > 1). Empty = the spec default; a
                      chosen value must round-trip through patchNode/flowToTray
                      or every Save resets it (the node-level-fields rule). */}
                  {(spec?.supported_modes?.length ?? 0) > 1 && (
                    <div className="space-y-1">
                      <Label
                        htmlFor="node-execution-mode"
                        className="text-[11px] text-muted-foreground"
                      >
                        Execution mode
                      </Label>
                      <select
                        id="node-execution-mode"
                        className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                        value={node.execution_mode ?? ""}
                        onChange={(e) =>
                          updateSetting({
                            execution_mode: e.target.value
                              ? (e.target.value as "per_item" | "once_on_batch")
                              : null,
                          })
                        }
                      >
                        <option value="">
                          Default ({spec?.default_mode === "once_on_batch"
                            ? "once with all items"
                            : "once per item"})
                        </option>
                        {(spec?.supported_modes ?? []).map((m) => (
                          <option key={m} value={m}>
                            {m === "per_item" ? "Run once per item" : "Run once with all items"}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label htmlFor="node-on-error" className="text-[11px] text-muted-foreground">
                      On error
                    </Label>
                    <select
                      id="node-on-error"
                      className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                      value={node.on_error ?? "stop"}
                      onChange={(e) =>
                        updateSetting({
                          on_error: e.target.value as
                            | "stop"
                            | "continue"
                            | "continue_error_output",
                        })
                      }
                    >
                      <option value="stop">Stop workflow</option>
                      <option value="continue">Continue</option>
                      <option value="continue_error_output">
                        Continue (using error output)
                      </option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="node-notes" className="text-[11px] text-muted-foreground">
                      Notes
                    </Label>
                    <textarea
                      id="node-notes"
                      rows={2}
                      className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                      value={node.notes ?? ""}
                      onChange={(e) => updateSetting({ notes: e.target.value || null })}
                      placeholder="Operator notes (no runtime effect)"
                    />
                  </div>
                </div>
              )}

              {paramTab === "parameters" && (
                <>
                  {/* ADR-0011 — Action switcher for Integration nodes
                      (Postgres select↔insert↔update↔…). Hidden for
                      standalone nodes and in read-only mode. */}
                  {!readOnly && siblingActions.length > 1 && (
                    <div className="mb-4 space-y-1.5 border-b border-border/60 pb-4">
                      <Label>
                        <span className="font-mono text-xs">action</span>
                      </Label>
                      <Combobox
                        options={actionOptions}
                        value={node!.kind}
                        onChange={switchAction}
                        placeholder="Pick an action"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Switch the {integrationId} action — shared fields are kept.
                      </p>
                    </div>
                  )}

                  {ingressType && (
                    <div className="mb-4 space-y-1.5 border-b border-border/60 pb-4">
                      <Label>
                        <span className="font-mono text-xs">ingress source</span>
                        <span className="text-danger"> *</span>
                      </Label>
                      <Combobox
                        options={ingressOptions}
                        value={(node.config.ingress_source_id as string) || null}
                        onChange={(v) => updateIngressBinding(v)}
                        placeholder={`Pick a ${ingressType} source`}
                        loading={ingressQuery.isLoading}
                        emptyText={`No ${ingressType} sources defined.`}
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Manage sources in the{" "}
                        <a href="/ingress" className="underline">
                          Ingress
                        </a>{" "}
                        page.
                      </p>
                    </div>
                  )}

                  {/* CALL_MAPPING gets a dedicated editor: mapping picker
                  + dynamic per-input JSONata bindings driven by the
                  selected mapping's declared signature. The generic
                  loop below skips its config fields. */}
                  {node!.kind === "call_mapping" && (
                    <div className="mb-4 border-b border-border/60 pb-4">
                      <CallMappingEditor
                        config={node!.config}
                        onChange={(next) => onChange(node!.id, { config: next })}
                      />
                    </div>
                  )}

                  {/* SWITCH gets a custom Cases editor instead of the
                  generic "render a JSON textarea for the list field"
                  fallback. The fields we render specially are skipped
                  by the generic loop below via SPECIAL_FIELDS. */}
                  {node!.kind === "switch" && (
                    <div className="mb-4 border-b border-border/60 pb-4">
                      <SwitchCasesEditor
                        cases={(node!.config.cases as SwitchCase[]) ?? []}
                        defaultHandleId={(node!.config.default_handle_id as string) || "default"}
                        onChange={({ cases, default_handle_id }) => {
                          onChange(node!.id, {
                            config: {
                              ...node!.config,
                              cases,
                              default_handle_id,
                            },
                          });
                        }}
                        outgoingEdgeHandles={outgoingEdgeHandles}
                        conditionFields={conditionFields}
                        jsBindingsDts={jsBindingsDts}
                      />
                    </div>
                  )}

                  {/* ADR-0008 PR3 — http_sink + connector_sink get a
                  structured editor for outcome_rules (http_sink only,
                  the connector response shape is connector-specific in
                  PR3 backend) and branches (both). Same skip pattern
                  in the generic loop below. */}
                  {(node!.kind === "http_sink" || node!.kind === "connector_sink") && (
                    <div className="mb-4 border-b border-border/60 pb-4 space-y-5">
                      {node!.kind === "http_sink" && (
                        <OutcomeRulesEditor
                          rules={(node!.config.outcome_rules as OutcomeRule[]) ?? []}
                          branchIds={((node!.config.branches as SinkBranch[]) ?? []).map(
                            (b) => b.id,
                          )}
                          onChange={(rules) => {
                            onChange(node!.id, {
                              config: { ...node!.config, outcome_rules: rules },
                            });
                          }}
                        />
                      )}
                      <BranchesEditor
                        branches={(node!.config.branches as SinkBranch[]) ?? []}
                        onChange={(branches) => {
                          onChange(node!.id, {
                            config: { ...node!.config, branches },
                          });
                        }}
                      />
                    </div>
                  )}

                  {/* ADR-0069 — the `image` node gets a dedicated sectioned
                  inspector with a live preview. Its NESTED config (resize /
                  crop / rotate / adjust / output + the overlays union list)
                  can't be rendered by the generic loop (empty "black box"
                  inputs); the loop skips every image config field below. */}
                  {node!.kind === "image" && (
                    <ImageNodeEditor
                      config={node!.config}
                      readOnly={readOnly}
                      jsAmbientTypes={jsBindingsDts}
                      onChange={(next) => onChange(node!.id, { config: next })}
                    />
                  )}

                  <div className="space-y-4">
                    {(() => {
                      // Render one field (or null when it's handled by a
                      // dedicated editor above / hidden for this kind).
                      const renderOne = (
                        name: string,
                        prop: JsonSchemaProperty,
                      ): React.ReactNode => {
                        // Conditional visibility (ADR-0009 x-tray-show-when):
                        // hide unless every sibling-field value matches.
                        const showWhen = prop["x-tray-show-when"];
                        if (showWhen) {
                          const visible = Object.entries(showWhen).every(([dep, want]) => {
                            const actual = node!.config[dep];
                            return Array.isArray(want) ? want.includes(actual) : actual === want;
                          });
                          if (!visible) return null;
                        }
                        if (
                          ingressType &&
                          (name === "ingress_source_id" || name === "ingress_source_name")
                        ) {
                          return null;
                        }
                        // Skip fields rendered by node-specific custom forms above.
                        if (
                          node!.kind === "switch" &&
                          (name === "cases" || name === "default_handle_id")
                        ) {
                          return null;
                        }
                        // ADR-0069 — the image node's whole config is owned by
                        // the dedicated ImageNodeEditor above.
                        if (node!.kind === "image" && IMAGE_CONFIG_FIELDS.includes(name)) {
                          return null;
                        }
                        // ADR-0008 PR3 — outcome_rules + branches are rendered
                        // by the dedicated OutcomeRulesEditor / BranchesEditor
                        // above. Generic loop would otherwise render them as
                        // JSON textareas (exactly the experience we're fixing).
                        if (
                          (node!.kind === "http_sink" || node!.kind === "connector_sink") &&
                          name === "branches"
                        ) {
                          return null;
                        }
                        if (node!.kind === "http_sink" && name === "outcome_rules") {
                          return null;
                        }
                        // call_mapping has a dedicated editor above; the
                        // generic loop would only render the dict fields
                        // as JSON textareas, which is precisely the
                        // experience we're trying to avoid.
                        if (
                          node!.kind === "call_mapping" &&
                          (name === "mapping_id" ||
                            name === "input_bindings" ||
                            name === "default_values" ||
                            name === "on_miss" ||
                            name === "output_path" ||
                            name === "output_field")
                        ) {
                          return null;
                        }
                        const value = node.config[name];
                        const t = effectiveType(prop);
                        // ADR-0009 — the backend marker is the single source of
                        // truth for whether a field is a template / code / fixed.
                        const exprKind = prop["x-tray-expression"];
                        const id = `cfg-${name}`;
                        const isRequired = required.has(name);
                        const credentialTypes = prop["x-credential-type"] as
                          | CredentialType[]
                          | undefined;

                        return (
                          <div key={name} className="space-y-1.5">
                            <Label htmlFor={id} className="inline-flex items-center gap-1">
                              <span className="font-mono text-xs">{name}</span>
                              {isRequired && <span className="text-danger">*</span>}
                              {prop.description && (
                                <Tooltip content={prop.description} side="right">
                                  <span
                                    tabIndex={0}
                                    className="cursor-help text-muted-foreground/70 hover:text-foreground"
                                    aria-label={`Help for ${name}`}
                                  >
                                    <Info className="h-3 w-3" strokeWidth={1.5} />
                                  </span>
                                </Tooltip>
                              )}
                            </Label>
                            {prop["x-tray-picker"] ? (
                              // Smart picker takes precedence over every other
                              // rendering — the backend has explicitly told us
                              // this field has a known reference type.
                              <SmartPicker
                                id={id}
                                pickerKind={prop["x-tray-picker"]}
                                value={value}
                                onChange={(v) => updateConfig(name, v)}
                                config={node!.config}
                                nodesInCanvas={nodesInCanvas}
                                currentNodeId={node!.id}
                                appId={appId}
                              />
                            ) : credentialTypes ? (
                              <CredentialPicker
                                id={id}
                                value={(value as string | null) ?? null}
                                onChange={(v) => updateConfig(name, v)}
                                type={credentialTypes}
                              />
                            ) : prop["x-tray-condition"] ? (
                              // Boolean predicate (filter/if): the SAME visual
                              // condition builder the page builder uses, in RAW
                              // mode (no braces). Expression mode is the raw JS
                              // Monaco editor. Identical UX across both runtimes.
                              <ConditionEditor
                                value={(value as string) ?? ""}
                                onChange={(v) => updateConfig(name, v)}
                                fields={conditionFields}
                                braces={false}
                                renderExpression={(v, oc) => (
                                  <div className={cn("w-full", MULTILINE_FIELDS.has(name) ? "h-56" : "h-24")}>
                                    <CodeEditor
                                      value={v}
                                      onChange={oc}
                                      language="javascript"
                                      height="100%"
                                      jsAmbientTypes={jsBindingsDts}
                                    />
                                  </div>
                                )}
                              />
                            ) : exprKind === "code" ? (
                              // CODE field (ADR-0009): always raw JS in Monaco,
                              // no toggle. Driven by the backend marker. Checked
                              // BEFORE the type branches so a code field never
                              // degrades to a plain input.
                              <div
                                className={cn(
                                  "w-full",
                                  MULTILINE_FIELDS.has(name) ? "h-56" : "h-24",
                                )}
                              >
                                <CodeEditor
                                  value={(value as string) ?? ""}
                                  onChange={(v) => updateConfig(name, v)}
                                  language="javascript"
                                  height="100%"
                                  jsAmbientTypes={jsBindingsDts}
                                />
                              </div>
                            ) : t === "object" &&
                              (exprKind === "template" ||
                                (prop.additionalProperties as { type?: string } | undefined)
                                  ?.type === "string") ? (
                              // A STRING-VALUED dict → key/value table: template
                              // dicts (headers / query_parameters / body_fields,
                              // values are {{ }} templates — string by
                              // construction) and declared dict[str,str]
                              // (additionalProperties.type == "string").
                              <KeyValueEditor
                                id={id}
                                value={value as Record<string, string> | null}
                                onChange={(v) => updateConfig(name, v)}
                                valuePlaceholder={
                                  exprKind === "template"
                                    ? "value (supports {{ }})"
                                    : "value"
                                }
                              />
                            ) : t === "object" ? (
                              // dict[str,Any] (response_data, connector_config,
                              // plugin_config): a KeyValueEditor would FLATTEN
                              // nested values to "[object Object]" on the first
                              // edit — render a JSON textarea instead.
                              <JsonObjectField
                                id={id}
                                value={value as Record<string, unknown> | null}
                                onChange={(v) => updateConfig(name, v)}
                              />
                            ) : exprKind === "template" ? (
                              // TEMPLATE field (ADR-0009): per-field Fixed | ƒx
                              // toggle + {{ }} interpolation. Driven by the marker.
                              // Takes precedence over the enum/number/boolean
                              // branches so a *liberal numeric* field (declared
                              // ``int | str`` → effectiveType "integer") still
                              // offers the ƒx toggle, not a bare number input.
                              <ExpressionField
                                id={id}
                                multiline={MULTILINE_FIELDS.has(name)}
                                value={value !== undefined && value !== null ? String(value) : ""}
                                onChange={(v) => updateConfig(name, v)}
                                jsAmbientTypes={jsBindingsDts}
                                registerInsert={registerInsert}
                                resolvePreview={resolvePreview}
                              />
                            ) : t === "array" && prop.items?.enum ? (
                              // Array of enum literals (e.g. row_changed's
                              // ``events: list[Literal["insert","update",
                              // "delete"]]``) → multi-checkbox group. The
                              // value is the selected-string array; toggling
                              // a box adds/removes that option (order follows
                              // the schema, not click order).
                              <div className="flex flex-wrap gap-3">
                                {(prop.items.enum ?? []).map((opt) => {
                                  const optStr = String(opt);
                                  const selected = Array.isArray(value)
                                    ? (value as unknown[]).map(String)
                                    : [];
                                  const checked = selected.includes(optStr);
                                  return (
                                    <label
                                      key={optStr}
                                      className="flex items-center gap-1.5 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const next = e.target.checked
                                            ? (prop.items!.enum ?? [])
                                                .map(String)
                                                .filter(
                                                  (o) =>
                                                    selected.includes(o) ||
                                                    o === optStr,
                                                )
                                            : selected.filter((o) => o !== optStr);
                                          updateConfig(name, next);
                                        }}
                                      />
                                      <span>{optStr}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : t === "array" &&
                              (prop.items as { type?: string } | undefined)?.type !==
                                "object" ? (
                              // FREE-FORM string list (sandbox_code.libraries /
                              // output_files, …). A plain Input here sent a bare
                              // STRING where the backend expects a list → 422 on
                              // save. One row per entry + add/remove; blanks
                              // dropped on commit. Array-of-OBJECT fields keep
                              // the fallback (their editors are node-specific).
                              <StringListField
                                id={id}
                                value={Array.isArray(value) ? (value as unknown[]).map(String) : []}
                                onChange={(v) => updateConfig(name, v)}
                              />
                            ) : t === "enum" ? (
                              <select
                                id={id}
                                className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                                value={String(value ?? "")}
                                onChange={(e) => updateConfig(name, e.target.value)}
                              >
                                <option value="">—</option>
                                {(prop.enum ?? []).map((opt) => (
                                  <option key={String(opt)} value={String(opt)}>
                                    {String(opt)}
                                  </option>
                                ))}
                              </select>
                            ) : t === "boolean" ? (
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  id={id}
                                  type="checkbox"
                                  checked={!!value}
                                  onChange={(e) => updateConfig(name, e.target.checked)}
                                />
                                <span className="text-muted-foreground">Enable</span>
                              </label>
                            ) : t === "integer" || t === "number" ? (
                              <Input
                                id={id}
                                type="number"
                                className="bg-background"
                                value={value !== undefined ? String(value) : ""}
                                min={prop.minimum}
                                max={prop.maximum}
                                onChange={(e) =>
                                  updateConfig(
                                    name,
                                    e.target.value === "" ? undefined : Number(e.target.value),
                                  )
                                }
                              />
                            ) : MULTILINE_FIELDS.has(name) ? (
                              <textarea
                                id={id}
                                rows={Math.max(
                                  3,
                                  Math.min(10, Math.ceil(String(value ?? "").length / 60)),
                                )}
                                className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs"
                                value={(value as string) ?? ""}
                                onChange={(e) => updateConfig(name, e.target.value)}
                              />
                            ) : (
                              // Plain single-line string read verbatim (cron
                              // expr, path, etc.) — no expression affordance.
                              <Input
                                id={id}
                                className="bg-background"
                                value={(value as string) ?? ""}
                                onChange={(e) => updateConfig(name, e.target.value)}
                              />
                            )}
                          </div>
                        );
                      };

                      // Group fields by their x-tray-group (n8n-style
                      // sections). Ungrouped fields render top-level; the
                      // first member of a group renders the whole titled
                      // box at that position, pulling in its siblings.
                      const entries = Object.entries(properties);
                      const seenGroups = new Set<string>();
                      const units: React.ReactNode[] = [];
                      for (const [name, prop] of entries) {
                        const group = prop["x-tray-group"];
                        if (!group) {
                          const el = renderOne(name, prop);
                          if (el) units.push(el);
                          continue;
                        }
                        if (seenGroups.has(group)) continue;
                        seenGroups.add(group);
                        const rendered = entries
                          .filter(([, p]) => p["x-tray-group"] === group)
                          .map(([n, p]) => renderOne(n, p))
                          .filter(Boolean);
                        if (rendered.length === 0) continue;
                        units.push(
                          <div
                            key={`grp:${group}`}
                            className="rounded-md border border-border/70 p-3"
                          >
                            <div className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {group}
                            </div>
                            <div className="space-y-4">{rendered}</div>
                          </div>,
                        );
                      }
                      return units;
                    })()}
                    {Object.keys(properties).length === 0 && (
                      <div className="rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted-foreground">
                        This node has no configurable parameters.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Params / Output divider */}
          <ResizeHandle
            onResize={(dx) =>
              // Dragging RIGHT shrinks Output; LEFT grows it. Hence
              // the inverted sign vs. the Input handle.
              setOutputW((w) => Math.max(240, Math.min(800, w - dx)))
            }
          />

          {/* OUTPUT PANE */}
          <OutputPane
            result={runResult}
            running={executeMutation.isPending}
            tab={outputTab}
            onTabChange={setOutputTab}
          />
        </div>

        {/* ADR-0067 S4 — interactive debugger overlay (js_code_transform only). Covers
            the drawer body; steps the code buffer on a sample input, pure-JS dry-run. */}
        {debugOpen && node.kind === "js_code_transform" && (
          <div className="absolute inset-0 z-30 flex flex-col bg-surface">
            <CodeNodeDebugPanel
              workflowId={workflowId}
              nodeId={node.id}
              code={String(node.config.code ?? "")}
              onClose={() => setDebugOpen(false)}
            />
          </div>
        )}
      </DialogContent>

      {/* Inline trigger-strategy prompt — only mounts when the
          Execute Node mutation discovered a manual/webhook trigger
          that needs user input. Nested Dialog so it overlays the
          main modal cleanly. */}
      <StrategyPromptDialog
        strategy={promptStrategy}
        workflowId={workflowId}
        onCancel={() => setPromptStrategy(null)}
        onRun={(payload, responseToken) => {
          setPromptStrategy(null);
          if (responseToken) {
            pendingRespondTokenRef.current = responseToken;
          }
          executeMutation.mutate(payload);
        }}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// INPUT pane — recent-payloads picker + manual JSON editor.
// ---------------------------------------------------------------------------

function InputPane({
  inputText,
  onInputTextChange,
  error,
  upstreamNodes,
  nodeOutputs,
  nodeLogOutputs,
  onInsert,
  logOutputs,
}: {
  inputText: string;
  onInputTextChange: (v: string) => void;
  error: string | null;
  upstreamNodes: UpstreamNode[];
  nodeOutputs: Record<string, unknown>;
  nodeLogOutputs: Record<string, Record<string, unknown>>;
  /** Per-iteration windows when this node ran inside a loop (null = ran once).
   *  They slice the VIEW only — Edit stays the whole buffer, because that IS
   *  the payload "Execute Node" sends. */
  logOutputs?: Record<string, unknown> | null;
  /** F4 (ADR-0068) — click-to-insert a `{{ … }}` binding into the focused ƒx
   *  field. The SCHEMA LENS is the picker (n8n model): with this set, its
   *  field rows are draggable into an ƒx field and clickable to insert. The
   *  old "Campi"/"Dati" switcher (a separate binding tree) is GONE — one tree,
   *  the schema, wearing both hats. Unset ⇒ view-only pane. */
  onInsert?: (expr: string) => void;
}) {
  // ADR-0012 — INPUT source picker (n8n's "N nodes back"). null = the
  // editable Run input (default); a node id = view that node's output
  // from the last run, read-only.
  const [sourceId, setSourceId] = React.useState<string | null>(null);

  // Keep an explicit choice while it's still a real upstream node; otherwise
  // fall back to "Input for run".
  //
  // It used to DEFAULT to the nearest upstream node that had data whenever this
  // node's own input was empty — the intent being "show real data instead of an
  // empty pane". The owner rejected it, rightly: opening a node and silently
  // landing on a DIFFERENT node's output is a surprise, and it made the Run
  // selector look like it only existed on "Input for run" (that branch is the
  // only one that rendered it). Default to this node's own input; choosing an
  // upstream node stays a deliberate act.
  React.useEffect(() => {
    setSourceId((cur) => (cur && upstreamNodes.some((n) => n.id === cur) ? cur : null));
  }, [upstreamNodes]);

  const picked = sourceId ? upstreamNodes.find((n) => n.id === sourceId) : null;
  const pickedData = sourceId ? nodeOutputs[sourceId] : undefined;
  const hasData = sourceId !== null && sourceId in nodeOutputs;

  return (
    <section className="node-editor-sidebar flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Input
        </span>
      </div>
      {/* A COLUMN with a pinned head and ONE scrolling body — NOT a single
          `overflow-y-auto` stack. With the stack, the source picker, the
          View/Edit toggle and the run selector scrolled away with the data,
          and each added row pushed the whole pane past its height (the owner:
          "quando c'è il selettore Runs le scroll-area aumentano"). The chrome
          is now fixed and only the viewer scrolls, inside itself — same shape
          as the Output pane. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-3">
        {/* Source picker — choose the Run input or an upstream node's
            output to inspect (the data you'd reach via $('Label')). Uses
            the styled Combobox (label + "N nodes back" subline). */}
        {upstreamNodes.length > 0 && (
          <Combobox
            options={[
              {
                value: "__run__",
                label: "Input for run",
                description: "Editable JSON for this test run",
              },
              ...upstreamNodes.map((n) => ({
                value: n.id,
                label: n.label,
                description: nodesBackLabel(n.distance),
              })),
            ]}
            value={sourceId ?? "__run__"}
            onChange={(v) => setSourceId(v && v !== "__run__" ? v : null)}
            placeholder="Input for run"
          />
        )}

        {picked ? (
          hasData ? (
            // An upstream node's OUTPUT is just as likely to be a merged loop
            // array as this node's input, so it pages its iterations too. Its
            // Schema lens binds under `$('Label').item.json`.
            <RunPagedViewer
              payload={pickedData}
              logOutputs={nodeLogOutputs[picked.id]}
              side="out"
              fill
              exprBase={onInsert ? upstreamBase(picked.label) : undefined}
              onPickBinding={onInsert}
            />
          ) : (
            <div className="rounded-md border border-dashed border-border/60 px-3 py-6 text-center text-[11px] text-muted-foreground">
              No data for <span className="font-medium text-foreground">{picked.label}</span> yet.
              Run the workflow (or up to here) to capture its output.
            </div>
          )
        ) : (
          /* Editable Run input — View (n8n-style viewer) by default;
             switch to Edit to modify. Auto-flips to Edit on a parse error.
             Its Schema lens binds under `$json`. */
          <InputPaneBody
            inputText={inputText}
            onInputTextChange={onInputTextChange}
            error={error}
            logOutputs={logOutputs}
            exprBase={onInsert ? "$json" : undefined}
            onPickBinding={onInsert}
          />
        )}
      </div>
    </section>
  );
}

/**
 * A payload viewer that PAGES the node's loop iterations when it ran more than
 * once — the selector + the window slice + the viewer, in one place.
 *
 * It exists because the two Input surfaces need identical behaviour: this
 * node's own input AND an upstream node's output are both merged loop arrays.
 * The selector used to live only in the "Input for run" branch, so picking an
 * upstream node silently lost the ability to page its iterations (the owner
 * reported it as "the runs dropdown only shows on Input for run"). One
 * component, so they can't drift.
 *
 * `side` picks which half of the window to read: "in" for a node's input array,
 * "out" for its output.
 */
function RunPagedViewer({
  payload,
  logOutputs,
  side,
  fill = false,
  exprBase,
  onPickBinding,
}: {
  payload: unknown;
  logOutputs?: Record<string, unknown> | null;
  side: "in" | "out";
  fill?: boolean;
  /** Turns the viewer's Schema lens into the draggable binding picker —
   *  set only on the Input-pane call sites (see ItemBatchViewer). */
  exprBase?: string;
  onPickBinding?: (binding: string) => void;
}) {
  const runs = React.useMemo(() => runSlicesOf(logOutputs), [logOutputs]);
  const [selectedRun, setSelectedRun] = React.useState<number | null>(null);
  // "Tutte" whenever the runs change (a new node / a new test-run) — a stale
  // iteration index would silently window into a different array.
  React.useEffect(() => setSelectedRun(null), [runs]);

  const activeRun = runs?.find((r) => r.run === selectedRun) ?? null;
  const shown = activeRun
    ? sliceRun(
        payload,
        side === "in" ? activeRun.in_offset : activeRun.out_offset,
        side === "in" ? activeRun.items_in : activeRun.items_out,
      )
    : payload;

  return (
    // When filling, the selector + error are PINNED (shrink-0) and only the
    // viewer scrolls — adding a selector must not make the whole pane scroll.
    <div className={cn("flex min-h-0 flex-col gap-1.5", fill && "flex-1")}>
      {runs && (
        <div className="shrink-0">
          <RunSelector
            runs={runs}
            logOutputs={logOutputs}
            selected={selectedRun}
            onSelect={setSelectedRun}
            side={side}
          />
        </div>
      )}
      {/* A run's error belongs to the ITERATION, not to a pane — so it shows on
          whichever side you're paging. The merged trace's status is
          worst-wins, which hides WHICH iteration broke; this is the only place
          that says it. */}
      {activeRun?.error && (
        <Alert tone="danger" title={`Iterazione ${activeRun.run + 1}`} className="mb-0 shrink-0">
          <pre className="whitespace-pre-wrap text-[11px]">{activeRun.error}</pre>
        </Alert>
      )}
      <ItemBatchViewer
        key={selectedRun ?? "all"}
        payload={shown}
        fill={fill}
        exprBase={exprBase}
        onPickBinding={onPickBinding}
      />
    </div>
  );
}

function InputPaneBody({
  inputText,
  onInputTextChange,
  error,
  logOutputs,
  exprBase,
  onPickBinding,
}: {
  inputText: string;
  onInputTextChange: (v: string) => void;
  error: string | null;
  logOutputs?: Record<string, unknown> | null;
  exprBase?: string;
  onPickBinding?: (binding: string) => void;
}) {
  const [mode, setMode] = React.useState<"view" | "edit">("view");

  // Parse for the viewer. We trim because an all-whitespace textarea
  // counts as "nothing to view yet" (empty state), not a parse error.
  const parsed = React.useMemo<{ ok: true; value: unknown } | { ok: false }>(() => {
    const t = inputText.trim();
    if (t === "") return { ok: true, value: null };
    try {
      return { ok: true, value: JSON.parse(t) };
    } catch {
      return { ok: false };
    }
  }, [inputText]);

  // Force Edit mode when the JSON doesn't parse — staring at a
  // viewer that says "invalid JSON" while you can't fix it would
  // be infuriating.
  const effectiveMode: "view" | "edit" = parsed.ok ? mode : "edit";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5">
      <div className="flex shrink-0 items-center justify-between">
        <div className="text-[11px] font-medium text-muted-foreground">Input for Run</div>
        <div className="flex gap-0 rounded border border-border/60 text-[10px]">
          {(["view", "edit"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              disabled={!parsed.ok && m === "view"}
              className={cn(
                "px-2 py-0.5 capitalize transition",
                effectiveMode === m
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
                !parsed.ok && m === "view" && "cursor-not-allowed opacity-40",
              )}
              title={!parsed.ok && m === "view" ? "Fix the JSON to switch to view mode" : undefined}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      {/* The run selector pages the VIEW only — hence it lives INSIDE the view
          branch. Edit deliberately keeps the whole buffer: it IS the payload
          "Execute Node" sends, so slicing it would silently change what Run
          executes. Don't "simplify" the selector out to the pane level. */}
      {effectiveMode === "view" && parsed.ok ? (
        <RunPagedViewer
          payload={parsed.value}
          logOutputs={logOutputs}
          side="in"
          fill
          exprBase={exprBase}
          onPickBinding={onPickBinding}
        />
      ) : (
        <>
          <textarea
            value={inputText}
            onChange={(e) => onInputTextChange(e.target.value)}
            rows={14}
            spellCheck={false}
            className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
          />
          {error && <p className="text-[10px] text-danger">Invalid JSON: {error}</p>}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OUTPUT pane — Result / Logs / Trace tabs.
// ---------------------------------------------------------------------------

function OutputPane({
  result,
  running,
  tab,
  onTabChange,
}: {
  result: RunSingleNodeResponse | null;
  running: boolean;
  tab: "result" | "logs" | "trace";
  onTabChange: (t: "result" | "logs" | "trace") => void;
}) {
  return (
    <section className="node-editor-sidebar flex h-full min-h-0 flex-col border-l border-[#e0e0e0] dark:border-[#3e3e3e]">
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-2">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Output</div>
        {result && (
          <div className="flex items-center gap-1.5 text-[10px]">
            {result.status === "succeeded" ? (
              <CheckCircle2 className="h-3 w-3 text-success" strokeWidth={1.5} />
            ) : (
              <AlertCircle className="h-3 w-3 text-danger" strokeWidth={1.5} />
            )}
            <span className="font-mono text-muted-foreground">{result.duration_ms}ms</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      {result && (
        <div className="flex border-b border-border/60 text-[11px]">
          {(["result", "logs", "trace"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onTabChange(t)}
              className={cn(
                "border-b-2 px-3 py-1.5 capitalize transition",
                tab === t
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {running ? (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
            Running…
          </div>
        ) : !result ? (
          <div className="rounded-md border border-dashed border-border/60 px-3 py-6 text-center text-[11px] text-muted-foreground">
            Click <span className="font-medium">Run</span> in the header to execute this node with
            the input on the left.
          </div>
        ) : tab === "result" ? (
          <>
            {result.error && (
              <Alert tone="danger" title="Error" className="mb-2">
                <pre className="whitespace-pre-wrap text-[11px]">{result.error}</pre>
              </Alert>
            )}
            {result.output_payload !== null && result.output_payload !== undefined ? (
              // n8n-style multi-view (Schema / Table / JSON / Binary), paged by
              // loop iteration — the SAME component the Input pane uses, so the
              // two can't drift. `fill` stretches it to the full Output-pane
              // height (matching the Input tree) and lets each lens scroll.
              <RunPagedViewer
                payload={result.output_payload}
                logOutputs={result.log_outputs}
                side="out"
                fill
              />
            ) : (
              !result.error && (
                <div className="text-[11px] text-muted-foreground">
                  Node returned no payload (None / dropped). Check the Logs tab for side-effects
                  (e.g. enqueued delivery id).
                </div>
              )
            )}
          </>
        ) : tab === "logs" ? (
          Object.keys(result.log_outputs).length === 0 ? (
            <div className="text-[11px] text-muted-foreground">No log outputs.</div>
          ) : (
            <pre className="overflow-x-auto rounded bg-muted/50 p-2 text-[11px]">
              {JSON.stringify(result.log_outputs, null, 2)}
            </pre>
          )
        ) : (
          // trace tab
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
            <dt className="text-muted-foreground">Status</dt>
            <dd className="font-mono">{result.status}</dd>
            <dt className="text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" strokeWidth={1.5} /> Duration
            </dt>
            <dd className="font-mono">{result.duration_ms} ms</dd>
            <dt className="text-muted-foreground">Output kind</dt>
            <dd className="font-mono">
              {result.output_payload === null
                ? "null"
                : result.output_payload === undefined
                  ? "undefined"
                  : Array.isArray(result.output_payload)
                    ? "array"
                    : typeof result.output_payload}
            </dd>
          </dl>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Trace → OutputPane shape adapter
// ---------------------------------------------------------------------------

/** Convert a single per-node trace from test-run into the shape the
 *  OutputPane consumes (the old single-node-run response). Keeps the
 *  output pane component agnostic about which endpoint produced the
 *  data. */
function traceToResult(
  trace: TestRunNodeTrace,
  _runStatus: "succeeded" | "failed",
): RunSingleNodeResponse {
  // Node's own status wins over run-level status when present —
  // the run can be "failed" because a downstream node blew up while
  // this node's trace was still "succeeded".
  const status: "succeeded" | "failed" | "skipped" =
    trace.status === "succeeded" ? "succeeded" : trace.status === "skipped" ? "skipped" : "failed";
  return {
    status,
    output_payload: trace.output ?? null,
    error: trace.error,
    log_outputs: trace.log_outputs ?? {},
    duration_ms: trace.duration_ms,
  };
}

// ---------------------------------------------------------------------------
// StrategyPromptDialog — inline payload prompt for Execute Node.
// ---------------------------------------------------------------------------
//
// Surfaces a small nested dialog when the trigger strategy needs user
// input before running. Three branches:
//
//   * **manual_input** — JSON editor, Run when valid
//   * **external_hit** (webhook) — URL + curl copy-paste, plus a JSON
//     fallback editor for "fake the call locally" runs
//   * anything else — shouldn't reach here (auto_payload runs directly,
//     unknown gets a fallback message)

function StrategyPromptDialog({
  strategy,
  workflowId,
  onCancel,
  onRun,
}: {
  strategy: TriggerStrategy | null;
  workflowId: string;
  onCancel: () => void;
  /** ``responseToken`` is set when the run was triggered by a
   *  webhook capture — the parent then needs to publish the
   *  test-run result back to that token so curl sees it. */
  onRun: (payload: unknown, responseToken?: string) => void;
}) {
  if (!strategy) return null;
  // Branch by kind. Webhook gets a richer flow (mint URL + long-poll
  // until first hit); the others share the simple JSON editor.
  if (strategy.kind === "external_hit") {
    return (
      <WebhookListenDialog
        strategy={strategy}
        workflowId={workflowId}
        titlePrefix="Execute Node"
        onCancel={onCancel}
        onCapture={(body, token) => onRun(body, token)}
      />
    );
  }
  return (
    <ManualPayloadDialog
      strategy={strategy}
      workflowId={workflowId}
      onCancel={onCancel}
      onRun={(payload) => onRun(payload)}
    />
  );
}

function ManualPayloadDialog({
  strategy,
  workflowId,
  onCancel,
  onRun,
}: {
  strategy: TriggerStrategy;
  workflowId: string;
  onCancel: () => void;
  onRun: (payload: unknown) => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = React.useState<string>("{}");
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [selectedSample, setSelectedSample] = React.useState<string>("");
  const [saveName, setSaveName] = React.useState<string>("");
  React.useEffect(() => {
    setDraft("{}");
    setParseError(null);
    setSelectedSample("");
    setSaveName("");
  }, [strategy.node_id]);

  // Saved sample payloads — the SAME store the whole-workflow "Test run"
  // drawer uses (`workflow_samples`), so a payload saved there is offered
  // here and vice-versa. Keeps the two run surfaces aligned.
  const samplesQuery = useQuery({
    queryKey: ["workflow-samples", workflowId],
    queryFn: () => workflowsApi.listSamples(workflowId),
  });
  const saveSampleMutation = useMutation({
    mutationFn: (name: string) => {
      const payload = JSON.parse(draft); // validated by tryParse before enabling
      return workflowsApi.upsertSample(workflowId, { name, payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow-samples", workflowId] });
    },
  });
  const deleteSampleMutation = useMutation({
    mutationFn: (sampleId: string) => workflowsApi.deleteSample(workflowId, sampleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflow-samples", workflowId] });
      setSelectedSample("");
    },
  });

  function draftIsValidJson(): boolean {
    try {
      JSON.parse(draft);
      return true;
    } catch {
      return false;
    }
  }

  function tryRun() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : "invalid JSON");
      return;
    }
    setParseError(null);
    onRun(parsed);
  }

  const samples = samplesQuery.data ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:!max-w-xl p-0 flex flex-col">
        <div className="border-b border-border/60 px-5 py-3">
          <DialogTitle className="text-sm font-semibold">
            Execute Node — {strategy.label}
          </DialogTitle>
          <p className="mt-1 text-[11px] text-muted-foreground">{strategy.description}</p>
        </div>
        <div className="space-y-3 px-5 py-4">
          {/* Saved samples picker — mirrors the Test-run drawer. */}
          {samples.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs">Saved sample</Label>
              <div className="flex items-center gap-2">
                <select
                  className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-[11px]"
                  value={selectedSample}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedSample(id);
                    if (!id) return;
                    const s = samples.find((x) => x.id === id);
                    if (s) {
                      setDraft(JSON.stringify(s.payload, null, 2));
                      setSaveName(s.name);
                      setParseError(null);
                    }
                  }}
                >
                  <option value="">— pick one to load —</option>
                  {samples.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {selectedSample && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteSampleMutation.mutate(selectedSample)}
                    title="Delete this sample"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Payload (JSON)</Label>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={10}
              spellCheck={false}
              className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
            />
            {parseError && (
              <p className="text-[10px] text-danger">Invalid JSON: {parseError}</p>
            )}
          </div>

          {/* Save current payload as a sample. */}
          <div className="flex items-end gap-2 border-t border-border/60 pt-2">
            <div className="flex-1 space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Save current payload as
              </Label>
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. happy-path / edge-case"
                className="h-8 text-[11px]"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={
                !saveName.trim() || !draftIsValidJson() || saveSampleMutation.isPending
              }
              onClick={() => saveSampleMutation.mutate(saveName.trim())}
            >
              {saveSampleMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
              ) : (
                <Save className="h-3.5 w-3.5" strokeWidth={1.5} />
              )}
              Save
            </Button>
          </div>
          {saveSampleMutation.isError && (
            <p className="text-[10px] text-danger">
              Save failed: {(saveSampleMutation.error as Error)?.message}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-5 py-3">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={tryRun}>
            <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
            Run
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// WebhookListenDialog was extracted to its own file so the canvas
// TestRunDrawer can reuse it (B6 extension). See
// ``./WebhookListenDialog.tsx``.

// ---------------------------------------------------------------------------
// ResizeHandle — draggable vertical divider between modal panes.
// ---------------------------------------------------------------------------
//
// Sits inside the parent CSS grid as a fixed-width column. On mousedown
// we capture the pointer, then a window-level mousemove emits the
// per-frame ``dx`` (delta-x in pixels) to the parent so it can update
// its width state. We don't track the absolute width here — the parent
// owns it (so it can clamp + persist), we just relay deltas.
//
// Visual: 6px hit area (the grid cell), but only the centre 1px line is
// visible until hover, when the whole strip lights up. ``col-resize``
// cursor confirms the affordance.

function ResizeHandle({ onResize }: { onResize: (dx: number) => void }) {
  const draggingRef = React.useRef(false);
  const lastXRef = React.useRef(0);

  React.useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!draggingRef.current) return;
      const dx = e.clientX - lastXRef.current;
      lastXRef.current = e.clientX;
      if (dx !== 0) onResize(dx);
    }
    function onUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [onResize]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      className="group relative h-full cursor-col-resize select-none"
      onMouseDown={(e) => {
        draggingRef.current = true;
        lastXRef.current = e.clientX;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        e.preventDefault();
      }}
    >
      {/* The visible 1px line, centered in the 6px hit strip. */}
      <div
        className={cn(
          "node-editor-divider absolute inset-y-0 left-1/2 w-px -translate-x-1/2",
          "transition-colors",
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CenterSlideHandle — single grip at the top-center of the modal body.
// ---------------------------------------------------------------------------
//
// N8N-style affordance: ONE visible drag handle floating in the middle
// of the top edge so the user immediately sees the layout is flexible.
// Acts as a "slide" handle for the central Parameters column:
//   drag right → Input grows, Output shrinks → params shifts right (same width)
//   drag left  → Input shrinks, Output grows → params shifts left (same width)
//
// The per-divider thin lines still work as independent handles for
// users who want to resize asymmetrically; this one is the discoverable
// shortcut for "give me a different layout".

function CenterSlideHandle({ onSlide }: { onSlide: (dx: number) => void }) {
  const draggingRef = React.useRef(false);
  const lastXRef = React.useRef(0);

  React.useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!draggingRef.current) return;
      const dx = e.clientX - lastXRef.current;
      lastXRef.current = e.clientX;
      if (dx !== 0) onSlide(dx);
    }
    function onUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [onSlide]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      title="Drag to slide the Parameters column"
      className={cn(
        "absolute left-1/2 top-0 z-20 -translate-x-1/2",
        "flex h-5 w-12 items-center justify-center gap-1",
        "rounded-b-md border border-t-0 border-border/60 bg-muted/60",
        "cursor-col-resize select-none transition-colors",
        "hover:border-accent hover:bg-accent/15",
        "active:border-accent active:bg-accent/25",
      )}
      onMouseDown={(e) => {
        draggingRef.current = true;
        lastXRef.current = e.clientX;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        e.preventDefault();
      }}
    >
      {/* Three thin vertical bars — the universal "drag handle" glyph. */}
      <span className="h-2.5 w-[1.5px] rounded-full bg-foreground/50" />
      <span className="h-2.5 w-[1.5px] rounded-full bg-foreground/50" />
      <span className="h-2.5 w-[1.5px] rounded-full bg-foreground/50" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic field-loop helpers (W1 save-integrity) — a free-form string LIST
// and a dict[str,Any] JSON editor. Both are "rich editor emits a projection":
// they hold a LOCAL buffer (so a half-typed row / invalid JSON survives
// keystrokes) and only commit a valid value upward.
// ---------------------------------------------------------------------------

function StringListField({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  // Local buffer: a freshly-added empty row must survive re-renders even
  // though the committed value drops blanks.
  const [rows, setRows] = React.useState<string[]>(value.length ? value : []);
  const commit = (next: string[]) => {
    setRows(next);
    onChange(next.map((r) => r.trim()).filter((r) => r !== ""));
  };
  return (
    <div className="space-y-1.5">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input
            id={i === 0 ? id : undefined}
            className="bg-background"
            value={row}
            onChange={(e) => commit(rows.map((r, j) => (j === i ? e.target.value : r)))}
          />
          <button
            type="button"
            aria-label="Remove entry"
            className="rounded p-1 text-muted-foreground hover:text-danger"
            onClick={() => commit(rows.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        onClick={() => setRows([...rows, ""])}
      >
        + Add entry
      </button>
    </div>
  );
}

function JsonObjectField({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: Record<string, unknown> | null;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const [text, setText] = React.useState(() => JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = React.useState<string | null>(null);
  return (
    <div className="space-y-1">
      <textarea
        id={id}
        rows={Math.max(4, Math.min(12, text.split("\n").length))}
        spellCheck={false}
        className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
        value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          try {
            const parsed: unknown = JSON.parse(t);
            if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
              setError("must be a JSON object");
              return;
            }
            setError(null);
            onChange(parsed as Record<string, unknown>);
          } catch (err) {
            setError(err instanceof Error ? err.message : "invalid JSON");
          }
        }}
      />
      {error && <p className="text-[10px] text-danger">Invalid JSON: {error}</p>}
    </div>
  );
}
